import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import GoogleConnectRequired from '../components/GoogleConnectRequired';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { uploadToDrive, clearSession } from '../utils/driveUpload';
import {
  HardDrive, Folder, File, Image, FileText, FileVideo, FileAudio, Search,
  Upload, Plus, Download, Link2, Trash2, Star, ChevronRight, Grid3x3,
  List, Loader2, X, Edit2, Check, Clock, Users, RefreshCw,
  MoreVertical, Building2, ArrowUpDown, ChevronDown, Eye, Move,
} from 'lucide-react';

const VIEWS = [
  { id: 'shared-drives', label: '공유 드라이브', icon: Building2 },
  { id: 'root', label: '내 드라이브', icon: HardDrive },
  { id: 'recent', label: '최근 파일', icon: Clock },
  { id: 'starred', label: '별표', icon: Star },
  { id: 'shared', label: '공유됨', icon: Users },
];

const SORT_OPTIONS = [
  { id: 'name-asc',          label: '이름 (오름차순)' },
  { id: 'name-desc',         label: '이름 (내림차순)' },
  { id: 'modifiedTime-desc', label: '최근 수정순' },
  { id: 'modifiedTime-asc',  label: '오래된 순' },
  { id: 'size-desc',         label: '크기 큰 순' },
  { id: 'size-asc',          label: '크기 작은 순' },
];

const MIME_ICONS = {
  'application/vnd.google-apps.folder': Folder,
  'image/': Image,
  'video/': FileVideo,
  'audio/': FileAudio,
  'text/': FileText,
  'application/pdf': FileText,
};

const GDOC_EXT = {
  'application/vnd.google-apps.document':     'GDOC',
  'application/vnd.google-apps.spreadsheet':  'SHEET',
  'application/vnd.google-apps.presentation': 'SLIDE',
  'application/vnd.google-apps.form':         'FORM',
};

function getIcon(mimeType) {
  if (!mimeType) return File;
  for (const [k, v] of Object.entries(MIME_ICONS)) {
    if (mimeType === k || mimeType.startsWith(k)) return v;
  }
  return File;
}

function getExt(file) {
  if (GDOC_EXT[file.mimeType]) return GDOC_EXT[file.mimeType];
  const m = file.name?.match(/\.([^.]+)$/);
  return m ? m[1].toUpperCase() : '';
}

function fmtDuration(sec) {
  if (sec == null || !isFinite(sec)) return '계산 중';
  if (sec < 60) return `${Math.ceil(sec)}초`;
  if (sec < 3600) return `${Math.round(sec / 60)}분`;
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return m ? `${h}시간 ${m}분` : `${h}시간`;
}

function fmtSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
}

function fmtDate(str) {
  if (!str) return '';
  return new Date(str).toLocaleDateString('ko-KR', { year: '2-digit', month: 'short', day: 'numeric' });
}

function isFolder(f) { return f.mimeType === 'application/vnd.google-apps.folder'; }
function isImage(f)  { return f.mimeType?.startsWith('image/'); }
function isGoogleDoc(f) { return f.mimeType?.startsWith('application/vnd.google-apps.') && !isFolder(f); }

function bigThumb(url) {
  if (!url) return url;
  return url.replace(/=s\d+$/, '=s1200').replace(/=w\d+-h\d+/, '=w1200-h900');
}

const EXT_COLORS = {
  PDF: 'bg-red-600', GDOC: 'bg-blue-600', SHEET: 'bg-green-600', SLIDE: 'bg-orange-500', FORM: 'bg-purple-500',
  JPG: 'bg-sky-600', JPEG: 'bg-sky-600', PNG: 'bg-sky-600', GIF: 'bg-sky-600', WEBP: 'bg-sky-600',
  MP4: 'bg-violet-600', MOV: 'bg-violet-600', AVI: 'bg-violet-600',
  ZIP: 'bg-gray-500', RAR: 'bg-gray-500',
  XLS: 'bg-green-700', XLSX: 'bg-green-700', DOC: 'bg-blue-700', DOCX: 'bg-blue-700',
  PPT: 'bg-orange-600', PPTX: 'bg-orange-600',
};

function ExtBadge({ file }) {
  const ext = getExt(file);
  if (!ext || isFolder(file)) return null;
  const color = EXT_COLORS[ext] || 'bg-gray-600';
  return (
    <span className={`absolute bottom-1.5 right-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded ${color} leading-none`} style={{ color: '#fff' }}>
      {ext}
    </span>
  );
}

/**
 * FileIcon / CardThumb은 반드시 모듈 레벨에 둔다.
 * 컴포넌트 함수 안에서 정의하면 렌더마다 새 컴포넌트 타입이 되어 React가
 * 서브트리를 언마운트→재마운트한다. 그러면 첫 클릭(선택) 직후 아이콘 DOM이
 * 교체되어 두 번째 클릭이 다른 노드에 떨어지고 dblclick이 발생하지 않는다.
 */
function FileIcon({ file, size = 32 }) {
  const I = getIcon(file.mimeType);
  const color = isFolder(file) ? '#fbbf24' : isImage(file) ? '#60a5fa' : isGoogleDoc(file) ? '#4ade80' : '#9ca3af';
  return <I size={size} style={{ color }} />;
}

/**
 * 파인더/탐색기 아이콘 뷰를 따른다.
 * 아이콘 뒤에 배경 박스를 두지 않고 아이콘 자체를 크게 보여준다.
 * 이미지 파일만 썸네일을 띄우고, 그 경우에만 테두리를 준다.
 */
function CardThumb({ file, onPreview }) {
  const thumb = isImage(file) && file.thumbnailLink;
  return (
    <div className="relative w-full flex items-center justify-center" style={{ height: 76 }}>
      {thumb ? (
        <img
          src={file.thumbnailLink}
          alt={file.name}
          className="max-h-full max-w-full object-contain rounded border border-gray-700/60"
        />
      ) : (
        <FileIcon file={file} size={56} />
      )}
      <ExtBadge file={file} />
      {!isFolder(file) && (
        <button
          onClick={e => onPreview(file, e)}
          className="absolute inset-0 flex items-center justify-center rounded opacity-0 hover:opacity-100 hover:bg-black/25 transition-all"
        >
          <Eye size={18} className="text-white" />
        </button>
      )}
    </div>
  );
}

export default function DrivePage() {
  const [connected, setConnected]     = useState(null);
  const [viewMode, setViewMode]       = useState('shared-drives');
  const [gridView, setGridView]       = useState(false);  // 기본은 리스트 보기
  const [sortKey, setSortKey]         = useState('name-asc');
  const [sortOpen, setSortOpen]       = useState(false);
  const [files, setFiles]             = useState([]);
  const [sharedDrives, setSharedDrives] = useState([]);
  const [activeDriveId, setActiveDriveId] = useState(null);
  const [loading, setLoading]         = useState(true);
  const [refreshing, setRefreshing]   = useState(false);
  const [folderStack, setFolderStack] = useState([]);
  const [searchQ, setSearchQ]         = useState('');
  const [searching, setSearching]     = useState(false);
  const [checkedIds, setCheckedIds]   = useState(new Set());
  const [renaming, setRenaming]       = useState(null);
  const [renameVal, setRenameVal]     = useState('');
  const [uploading, setUploading]     = useState(false);
  const [upProgress, setUpProgress]   = useState(null); // { name, loaded, total, ratio, speed, eta }
  const uploadAbortRef                = useRef(null);
  const uploadFileRef                 = useRef(null);
  const [newFolderMode, setNewFolderMode] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [contextMenu, setContextMenu] = useState(null);
  const [preview, setPreview]         = useState(null);
  // 이동 모달
  const [moveModal, setMoveModal]     = useState(false);
  const [pickerStack, setPickerStack] = useState([]);
  const [pickerFiles, setPickerFiles] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [moving, setMoving]           = useState(false);
  const [deleting, setDeleting]       = useState(false);

  const fileInputRef = useRef();
  const navigate = useNavigate();
  const renameRef = useRef();

  // 단일 선택 파일 (detail panel용)
  const singleSelected = useMemo(() => {
    if (checkedIds.size !== 1) return null;
    const id = [...checkedIds][0];
    return files.find(f => f.id === id) || null;
  }, [checkedIds, files]);

  const anyChecked = checkedIds.size >= 1;
  const multiChecked = checkedIds.size >= 2;

  useEffect(() => {
    api.get('/google/status').then(r => {
      setConnected(r.data.connected);
      if (r.data.connected) loadFiles();
    }).catch(() => setConnected(false));
  }, []);

  const currentFolderId = folderStack.length > 0 ? folderStack[folderStack.length - 1].id : 'root';

  const loadFiles = useCallback(async (folderId = currentFolderId, view = viewMode, driveId = activeDriveId) => {
    setLoading(true);
    setCheckedIds(new Set());
    try {
      let r;
      if (view === 'shared-drives' && !driveId) {
        r = await api.get('/google/drive/drives');
        const drives = r.data.drives || [];
        setSharedDrives(drives);
        if (drives.length > 0) {
          const first = drives[0];
          setActiveDriveId(first.id);
          setFolderStack([{ id: first.id, name: first.name }]);
          const r2 = await api.get(`/google/drive/drives/${first.id}/files`);
          setFiles(r2.data.files || []);
        } else { setFiles([]); }
        setLoading(false); setRefreshing(false); return;
      } else if (view === 'shared-drives' && driveId) {
        r = await api.get(`/google/drive/drives/${driveId}/files`, { params: { folderId: folderId !== 'root' ? folderId : undefined } });
        setFiles(r.data.files || []);
      } else if (view === 'recent')  r = await api.get('/google/drive/recent');
      else if (view === 'starred')   r = await api.get('/google/drive/starred');
      else if (view === 'shared')    r = await api.get('/google/drive/shared');
      else r = await api.get('/google/drive/files', { params: { folderId } });
      if (view !== 'shared-drives') setFiles(r.data.files || []);
    } catch (e) {
      if (e.response?.status === 401) setConnected(false);
    } finally { setLoading(false); setRefreshing(false); }
  }, [currentFolderId, viewMode, activeDriveId]);

  useEffect(() => { if (connected) loadFiles(currentFolderId, viewMode); }, [viewMode, connected]);

  const sortedFiles = useMemo(() => {
    const [field, order] = sortKey.split('-');
    return [...files].sort((a, b) => {
      let va, vb;
      if (field === 'name') { va = a.name?.toLowerCase() || ''; vb = b.name?.toLowerCase() || ''; }
      else if (field === 'modifiedTime') { va = a.modifiedTime || ''; vb = b.modifiedTime || ''; }
      else if (field === 'size') { va = parseInt(a.size) || 0; vb = parseInt(b.size) || 0; }
      if (isFolder(a) && !isFolder(b)) return -1;
      if (!isFolder(a) && isFolder(b)) return 1;
      if (va < vb) return order === 'asc' ? -1 : 1;
      if (va > vb) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [files, sortKey]);

  // ── 클릭 핸들러 ──────────────────────────────────────────────────────────────

  /**
   * 더블클릭을 브라우저 dblclick이 아니라 클릭 간격으로 직접 판정한다.
   *
   * 첫 클릭으로 항목이 선택되면 우측 상세 패널이 열리면서 목록 영역이 좁아지고
   * 카드/행이 밀린다. 그러면 두 번째 클릭이 다른 요소에 떨어져 브라우저가
   * dblclick을 발생시키지 않는다(두 클릭의 target이 같아야 발생).
   * 파일 id + 시각으로 판정하면 레이아웃이 움직여도 항상 동작한다.
   */
  const lastClickRef = useRef({ id: null, at: 0 });
  const DBL_MS = 400;

  function handleItemClick(e, file) {
    e.stopPropagation();

    if (e.ctrlKey || e.metaKey) {
      lastClickRef.current = { id: null, at: 0 };
      setCheckedIds(prev => {
        const next = new Set(prev);
        if (next.has(file.id)) next.delete(file.id); else next.add(file.id);
        return next;
      });
      return;
    }

    const now = Date.now();
    const prev = lastClickRef.current;
    if (prev.id === file.id && now - prev.at < DBL_MS) {
      lastClickRef.current = { id: null, at: 0 };
      if (isFolder(file)) openFolder(file); else openPreview(file);
      return;
    }

    lastClickRef.current = { id: file.id, at: now };
    setCheckedIds(new Set([file.id]));
  }

  function toggleCheck(e, fileId) {
    e.stopPropagation();
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId); else next.add(fileId);
      return next;
    });
  }

  function handleSelectAll() {
    if (checkedIds.size === sortedFiles.length) {
      setCheckedIds(new Set());
    } else {
      setCheckedIds(new Set(sortedFiles.map(f => f.id)));
    }
  }

  // ── CRUD ─────────────────────────────────────────────────────────────────────

  async function handleSearch(e) {
    e.preventDefault();
    if (!searchQ.trim()) { loadFiles(); return; }
    setSearching(true);
    try {
      const r = await api.get('/google/drive/search', { params: { q: searchQ } });
      setFiles(r.data.files || []); setFolderStack([]);
    } finally { setSearching(false); }
  }

  function openSharedDrive(drv) {
    setActiveDriveId(drv.id);
    setFolderStack([{ id: drv.id, name: drv.name }]);
    loadFiles('root', 'shared-drives', drv.id);
  }

  function openFolder(file) {
    if (viewMode === 'shared-drives' && activeDriveId) {
      setFolderStack(prev => [...prev, { id: file.id, name: file.name }]);
      loadFiles(file.id, 'shared-drives', activeDriveId);
    } else {
      setFolderStack(prev => [...prev, { id: file.id, name: file.name }]);
      loadFiles(file.id, 'root'); setViewMode('root');
    }
  }

  function goToFolder(idx) {
    const next = folderStack.slice(0, idx + 1);
    setFolderStack(next);
    if (viewMode === 'shared-drives') {
      if (idx === 0) loadFiles('root', 'shared-drives', activeDriveId);
      else loadFiles(next[next.length - 1].id, 'shared-drives', activeDriveId);
    } else loadFiles(next.length > 0 ? next[next.length - 1].id : 'root', 'root');
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    // 브라우저가 구글로 직접 청크를 올린다 — 서버 용량 제한을 받지 않는다
    const controller = new AbortController();
    uploadAbortRef.current = controller;
    uploadFileRef.current = { file, folderId: currentFolderId };
    setUploading(true);

    const startedAt = Date.now();
    let startedFrom = null;
    setUpProgress({ name: file.name, loaded: 0, total: file.size, ratio: 0, speed: 0, eta: null });

    try {
      await uploadToDrive(file, currentFolderId, {
        signal: controller.signal,
        onProgress: ({ loaded, total, ratio }) => {
          // 이어받기로 건너뛴 구간은 속도 계산에서 제외한다
          if (startedFrom === null) startedFrom = loaded;
          const elapsed = (Date.now() - startedAt) / 1000;
          const speed = elapsed > 0 ? (loaded - startedFrom) / elapsed : 0;
          const eta = speed > 0 ? (total - loaded) / speed : null;
          setUpProgress({ name: file.name, loaded, total, ratio, speed, eta });
        },
      });
      await loadFiles();
    } catch (err) {
      if (err.name !== 'AbortError') alert('업로드 실패: ' + err.message);
    } finally {
      uploadAbortRef.current = null;
      uploadFileRef.current = null;
      setUploading(false);
      setUpProgress(null);
    }
  }

  function cancelUpload() {
    uploadAbortRef.current?.abort();
    // 세션을 남겨두면 같은 파일을 다시 고를 때 이어받는다.
    // 취소는 "그만두기"이므로 그 파일의 세션만 버린다.
    const cur = uploadFileRef.current;
    if (cur) clearSession(cur.file, cur.folderId);
  }

  async function handleNewFolder() {
    if (!newFolderName.trim()) return;
    await api.post('/google/drive/folders', { name: newFolderName.trim(), parentId: currentFolderId !== 'root' ? currentFolderId : undefined });
    setNewFolderMode(false); setNewFolderName(''); await loadFiles();
  }

  async function handleStar(file) {
    await api.patch(`/google/drive/files/${file.id}`, { starred: !file.starred });
    setFiles(prev => prev.map(f => f.id === file.id ? { ...f, starred: !f.starred } : f));
  }

  async function handleDelete(file) {
    if (!confirm(`'${file.name}'을(를) 휴지통으로 이동하시겠습니까?`)) return;
    await api.delete(`/google/drive/files/${file.id}`);
    setFiles(prev => prev.filter(f => f.id !== file.id));
    setCheckedIds(prev => { const n = new Set(prev); n.delete(file.id); return n; });
  }

  async function handleBatchDelete() {
    const ids = [...checkedIds];
    if (!confirm(`${ids.length}개 항목을 휴지통으로 이동하시겠습니까?`)) return;
    setDeleting(true);
    try {
      await api.post('/google/drive/files/batch-delete', { fileIds: ids });
      setFiles(prev => prev.filter(f => !checkedIds.has(f.id)));
      setCheckedIds(new Set());
    } catch (e) { alert('삭제 실패: ' + e.message); }
    finally { setDeleting(false); }
  }

  async function handleRename(fileId) {
    if (!renameVal.trim()) return;
    await api.patch(`/google/drive/files/${fileId}`, { name: renameVal.trim() });
    setFiles(prev => prev.map(f => f.id === fileId ? { ...f, name: renameVal.trim() } : f));
    setRenaming(null);
  }

  async function handleShare(file) {
    try {
      const r = await api.post(`/google/drive/files/${file.id}/share`, { role: 'reader' });
      const link = r.data.link;
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(link);
        } else {
          const el = document.createElement('textarea');
          el.value = link; el.style.position = 'fixed'; el.style.opacity = '0';
          document.body.appendChild(el); el.select();
          document.execCommand('copy'); document.body.removeChild(el);
        }
        alert('공유 링크가 클립보드에 복사되었습니다.');
      } catch {
        prompt('아래 링크를 복사하세요:', link);
      }
    } catch (e) { alert('공유 링크 생성 실패: ' + (e.response?.data?.message || e.message)); }
  }

  async function handleDownload(file) {
    if (isGoogleDoc(file)) { window.open(file.webViewLink, '_blank'); return; }
    try {
      const token = localStorage.getItem('mfg_token');
      const res = await fetch(`/api/google/drive/files/${file.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('다운로드 실패');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      alert('다운로드 중 오류가 발생했습니다.');
    }
  }

  function handleContextMenu(e, file) {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, file });
  }

  function openPreview(file, e) {
    e?.stopPropagation();
    if (isFolder(file)) { openFolder(file); return; }
    setPreview(file);
  }

  // ── 이동 모달 ────────────────────────────────────────────────────────────────

  async function openMoveModal() {
    setMoveModal(true);
    setPickerStack([]);
    loadPickerFolder('root');
  }

  async function loadPickerFolder(folderId) {
    setPickerLoading(true);
    try {
      const r = await api.get('/google/drive/files', { params: { folderId } });
      setPickerFiles((r.data.files || []).filter(f => isFolder(f)));
    } catch { setPickerFiles([]); }
    finally { setPickerLoading(false); }
  }

  function pickerOpenFolder(folder) {
    setPickerStack(prev => [...prev, { id: folder.id, name: folder.name }]);
    loadPickerFolder(folder.id);
  }

  function pickerGoTo(idx) {
    if (idx < 0) {
      setPickerStack([]);
      loadPickerFolder('root');
    } else {
      const next = pickerStack.slice(0, idx + 1);
      setPickerStack(next);
      loadPickerFolder(next[next.length - 1].id);
    }
  }

  async function handleMoveConfirm() {
    const targetId = pickerStack.length > 0 ? pickerStack[pickerStack.length - 1].id : 'root';
    const ids = [...checkedIds];
    setMoving(true);
    try {
      await api.post('/google/drive/files/batch-move', { fileIds: ids, targetFolderId: targetId });
      setFiles(prev => prev.filter(f => !checkedIds.has(f.id)));
      setCheckedIds(new Set());
      setMoveModal(false);
    } catch (e) { alert('이동 실패: ' + e.message); }
    finally { setMoving(false); }
  }

  // ── 이벤트 ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (contextMenu) {
      const close = () => setContextMenu(null);
      window.addEventListener('click', close);
      return () => window.removeEventListener('click', close);
    }
  }, [contextMenu]);

  useEffect(() => {
    const handler = e => {
      if (e.key === 'Escape') { setPreview(null); setSortOpen(false); setMoveModal(false); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  if (connected === false) return <GoogleConnectRequired message="드라이브를 사용하려면 Google 계정을 연결해야 합니다." />;

  const allChecked = sortedFiles.length > 0 && checkedIds.size === sortedFiles.length;

  return (
    <div className="flex h-[calc(100vh-52px)] overflow-hidden">
      {/* 사이드바 */}
      <div className="w-44 flex-shrink-0 border-r border-gray-800 flex flex-col bg-gray-900/50">
        <div className="p-3 space-y-1.5">
          <button onClick={() => fileInputRef.current?.click()} disabled={uploading} style={{ color: '#ffffff' }} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#00B2FF] hover:bg-[#0090D4] text-sm font-semibold transition-colors disabled:opacity-60">
            {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            {uploading ? '업로드 중...' : '파일 업로드'}
          </button>
          <input type="file" ref={fileInputRef} className="hidden" onChange={handleUpload} />
          {upProgress && (
            <div className="rounded-xl border border-gray-700 bg-gray-800/60 p-2.5 space-y-1.5">
              <div className="text-[11px] text-gray-300 truncate" title={upProgress.name}>{upProgress.name}</div>
              <div className="h-1.5 rounded-full bg-gray-700 overflow-hidden">
                <div className="h-full rounded-full transition-all duration-300"
                     style={{ width: `${Math.round(upProgress.ratio * 100)}%`, background: '#00B2FF' }} />
              </div>
              <div className="flex items-center justify-between text-[10px] text-gray-400">
                <span>{fmtSize(upProgress.loaded)} / {fmtSize(upProgress.total)}</span>
                <span>{Math.round(upProgress.ratio * 100)}%</span>
              </div>
              <div className="flex items-center justify-between text-[10px] text-gray-500">
                <span>{upProgress.speed > 0 ? `${fmtSize(upProgress.speed)}/s` : '연결 중'}</span>
                <span>남은 시간 {fmtDuration(upProgress.eta)}</span>
              </div>
              <button onClick={cancelUpload}
                      className="w-full py-1 rounded-lg border border-gray-700 hover:bg-gray-700 text-gray-300 text-[11px] font-medium transition-colors">
                취소
              </button>
            </div>
          )}
          <button onClick={() => { setNewFolderMode(true); setNewFolderName('새 폴더'); }} className="w-full flex items-center justify-center gap-2 py-2 rounded-xl border border-gray-700 hover:bg-gray-800 text-gray-300 text-sm font-medium transition-colors">
            <Plus size={13} /> 새 폴더
          </button>
        </div>
        <nav className="flex-1 px-2 space-y-0.5">
          {VIEWS.map(({ id, label, icon: I }) => (
            <button key={id}
              onClick={() => {
                setViewMode(id); setFolderStack([]); setSearchQ(''); setCheckedIds(new Set());
                if (id === 'shared-drives') { if (viewMode === 'shared-drives' && connected) loadFiles('root', 'shared-drives', null); }
                else setActiveDriveId(null);
              }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                viewMode === id && !searchQ ? 'bg-[#00B2FF]/15 text-[#00B2FF] font-semibold' : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
              }`}
            >
              <I size={14} /> {label}
            </button>
          ))}
        </nav>
      </div>

      {/* 메인 */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* 툴바 */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-800">
          <div className="flex items-center gap-1 text-sm flex-1 min-w-0">
            {viewMode !== 'shared-drives' && (
              <button onClick={() => { setFolderStack([]); setViewMode('root'); setSearchQ(''); setActiveDriveId(null); loadFiles('root', 'root'); }} className="text-gray-400 hover:text-gray-200">내 드라이브</button>
            )}
            {folderStack.map((f, i) => (
              <span key={f.id} className="flex items-center gap-1">
                {(viewMode !== 'shared-drives' || i > 0) && <ChevronRight size={12} className="text-gray-600" />}
                <button onClick={() => goToFolder(i)} className={`truncate max-w-[100px] ${i < folderStack.length - 1 ? 'text-gray-400 hover:text-gray-200' : 'text-gray-200 cursor-default'}`}>
                  {f.name}
                </button>
              </span>
            ))}
            {searchQ && <span className="text-gray-500 ml-1">— "{searchQ}" 검색 결과</span>}
          </div>

          <form onSubmit={handleSearch} className="flex gap-1.5">
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
              <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="파일 검색..."
                className="pl-7 pr-3 py-1.5 rounded-lg bg-gray-800 border border-gray-700 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-gray-500 w-44"
              />
            </div>
            {searchQ && <button type="button" onClick={() => { setSearchQ(''); loadFiles(); }} className="p-1.5 rounded hover:bg-gray-800 text-gray-500"><X size={13} /></button>}
          </form>

          {/* 정렬 드롭다운 */}
          <div className="relative">
            <button
              onClick={e => { e.stopPropagation(); setSortOpen(v => !v); }}
              className="flex items-center gap-1 p-1.5 rounded hover:bg-gray-800 text-gray-500 hover:text-gray-300 text-xs"
              title="정렬"
            >
              <ArrowUpDown size={13} />
              <ChevronDown size={10} />
            </button>
            {sortOpen && (
              <div className="absolute right-0 top-full mt-1 z-50 bg-gray-900 border border-gray-700 rounded-xl py-1 min-w-[160px] text-xs" onClick={e => e.stopPropagation()}>
                {SORT_OPTIONS.map(opt => (
                  <button key={opt.id} onClick={() => { setSortKey(opt.id); setSortOpen(false); }}
                    className={`w-full text-left px-3 py-2 hover:bg-gray-800 transition-colors ${sortKey === opt.id ? 'text-[#00B2FF] font-semibold' : 'text-gray-300'}`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button onClick={() => { setRefreshing(true); loadFiles(); }} disabled={refreshing} className="p-1.5 rounded hover:bg-gray-800 text-gray-500 hover:text-gray-300">
            <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
          </button>
          <button onClick={() => setGridView(v => !v)} className="p-1.5 rounded hover:bg-gray-800 text-gray-500 hover:text-gray-300">
            {gridView ? <List size={13} /> : <Grid3x3 size={13} />}
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4" onClick={() => { setCheckedIds(new Set()); setContextMenu(null); setSortOpen(false); }}>
            {newFolderMode && (
              <div className="mb-3 flex items-center gap-2">
                <Folder size={18} className="text-yellow-400" />
                <input autoFocus value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleNewFolder(); if (e.key === 'Escape') setNewFolderMode(false); }}
                  className="px-2 py-1 rounded bg-gray-800 border border-gray-600 text-sm text-gray-200 focus:outline-none"
                />
                <button onClick={handleNewFolder} className="p-1 rounded bg-green-600 hover:bg-green-500 text-white"><Check size={13} /></button>
                <button onClick={() => setNewFolderMode(false)} className="p-1 rounded hover:bg-gray-700 text-gray-400"><X size={13} /></button>
              </div>
            )}

            {/* 공유 드라이브 목록 */}
            {viewMode === 'shared-drives' && !activeDriveId && !loading && (
              sharedDrives.length === 0 ? (
                <div className="text-center py-20 text-gray-500 text-sm">공유 드라이브가 없습니다.</div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 p-4">
                  {sharedDrives.map(drv => (
                    <div key={drv.id} onClick={() => openSharedDrive(drv)}
                      className="flex flex-col items-center gap-2 p-4 rounded-xl border border-gray-700 hover:border-[#00B2FF]/50 hover:bg-[#00B2FF]/5 cursor-pointer transition-all">
                      <Building2 size={36} className="text-[#00B2FF]" />
                      <span className="text-sm text-gray-200 text-center font-medium truncate w-full">{drv.name}</span>
                    </div>
                  ))}
                </div>
              )
            )}

            {loading ? (
              <div className="flex items-center justify-center h-48"><Loader2 className="animate-spin text-gray-500" size={24} /></div>
            ) : (viewMode !== 'shared-drives' || activeDriveId) && sortedFiles.length === 0 ? (
              <div className="text-center py-20 text-gray-500 text-sm">파일이 없습니다.</div>
            ) : (viewMode !== 'shared-drives' || activeDriveId) && gridView ? (
              /* 그리드 뷰 */
              /* 고정 열 수 대신 auto-fill — 화면이 넓어지면 칸이 벌어지는 대신 열 개수가 늘어난다 */
              <div className="grid gap-1" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))' }}>
                {sortedFiles.map(file => {
                  const sel = checkedIds.has(file.id);
                  return (
                  <div key={file.id}
                    onClick={e => handleItemClick(e, file)}
                    onContextMenu={e => handleContextMenu(e, file)}
                    // 호버·선택 배경은 라이트/다크 모두에서 은은하게 (bg-gray-800/40은 라이트 리맵을 타지 않아 너무 진함)
                    className={`group relative rounded-lg cursor-pointer select-none px-1 pt-2 pb-1.5 flex flex-col items-center transition-colors ${
                      sel ? 'bg-[#00B2FF]/8' : 'hover:bg-gray-500/10'
                    }`}
                  >
                    {/* 체크박스 */}
                    <div
                      className={`absolute top-1.5 left-1.5 z-10 transition-opacity ${sel || anyChecked ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                      onClick={e => toggleCheck(e, file.id)}
                    >
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${sel ? 'bg-[#00B2FF] border-[#00B2FF]' : 'bg-gray-900/70 border-gray-500'}`}>
                        {sel && <Check size={9} className="text-white" strokeWidth={3} />}
                      </div>
                    </div>

                    <CardThumb file={file} onPreview={openPreview} />

                    {renaming === file.id ? (
                      <input ref={renameRef} autoFocus value={renameVal} onChange={e => setRenameVal(e.target.value)}
                        onBlur={() => handleRename(file.id)}
                        onKeyDown={e => { if (e.key === 'Enter') handleRename(file.id); if (e.key === 'Escape') setRenaming(null); }}
                        onClick={e => e.stopPropagation()}
                        className="mt-1.5 w-full text-xs text-center bg-gray-700 rounded px-1 py-0.5 focus:outline-none text-gray-200"
                      />
                    ) : (
                      // 파인더처럼 선택 시 이름에만 알약 하이라이트
                      <p className={`mt-1.5 text-xs leading-tight text-center line-clamp-2 break-all px-1.5 py-0.5 rounded ${
                        sel ? 'bg-[#00B2FF]/70 text-white' : 'text-gray-300'
                      }`}>
                        {file.name}
                      </p>
                    )}
                  </div>
                  );
                })}
              </div>
            ) : (viewMode !== 'shared-drives' || activeDriveId) && (
              /* 리스트 뷰 */
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 border-b border-gray-800">
                    <th className="pb-2 w-8 text-left">
                      <div
                        className="w-4 h-4 rounded border-2 flex items-center justify-center cursor-pointer transition-colors ml-1 mt-0.5"
                        style={{ borderColor: allChecked ? '#00B2FF' : '#6b7280', backgroundColor: allChecked ? '#00B2FF' : 'transparent' }}
                        onClick={e => { e.stopPropagation(); handleSelectAll(); }}
                      >
                        {allChecked && <Check size={9} className="text-white" strokeWidth={3} />}
                      </div>
                    </th>
                    <th className="text-left pb-2 font-medium">이름</th>
                    <th className="text-left pb-2 font-medium hidden sm:table-cell">수정일</th>
                    <th className="text-left pb-2 font-medium hidden md:table-cell">크기</th>
                    <th className="pb-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFiles.map(file => (
                    <tr key={file.id}
                      onClick={e => handleItemClick(e, file)}
                        onContextMenu={e => handleContextMenu(e, file)}
                      className={`border-b border-gray-800/40 cursor-pointer select-none transition-colors group ${checkedIds.has(file.id) ? 'bg-[#00B2FF]/8' : 'hover:bg-gray-500/10'}`}
                    >
                      <td className="py-2 w-8" onClick={e => toggleCheck(e, file.id)}>
                        <div
                          className={`w-4 h-4 rounded border-2 flex items-center justify-center ml-1 transition-all ${checkedIds.has(file.id) || anyChecked ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                          style={{ borderColor: checkedIds.has(file.id) ? '#00B2FF' : '#6b7280', backgroundColor: checkedIds.has(file.id) ? '#00B2FF' : 'transparent' }}
                        >
                          {checkedIds.has(file.id) && <Check size={9} className="text-white" strokeWidth={3} />}
                        </div>
                      </td>
                      <td className="py-2">
                        <div className="flex items-center gap-2">
                          <div className="relative flex-shrink-0">
                            <FileIcon file={file} size={18} />
                          </div>
                          {renaming === file.id ? (
                            <input autoFocus ref={renameRef} value={renameVal} onChange={e => setRenameVal(e.target.value)}
                              onBlur={() => handleRename(file.id)}
                              onKeyDown={e => { if (e.key === 'Enter') handleRename(file.id); if (e.key === 'Escape') setRenaming(null); }}
                              onClick={e => e.stopPropagation()}
                              className="flex-1 bg-gray-700 rounded px-1 py-0.5 text-sm focus:outline-none text-gray-200" />
                          ) : (
                            <span className="text-gray-100 truncate">{file.name}</span>
                          )}
                          {getExt(file) && !isFolder(file) && (
                            <span className={`flex-shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded leading-none ${EXT_COLORS[getExt(file)] || 'bg-gray-600'}`} style={{ color: '#fff' }}>
                              {getExt(file)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 text-gray-500 text-xs hidden sm:table-cell">{fmtDate(file.modifiedTime)}</td>
                      <td className="py-2 text-gray-500 text-xs hidden md:table-cell">{isFolder(file) ? '—' : fmtSize(parseInt(file.size))}</td>
                      <td className="py-2">
                        <button onClick={e => { e.stopPropagation(); setContextMenu({ x: e.clientX, y: e.clientY, file }); }}
                          className="p-1 rounded hover:bg-gray-700 text-gray-500 opacity-0 group-hover:opacity-100">
                          <MoreVertical size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* 우측 상세 패널 — 폭(w-56)을 항상 점유한다.
              선택 시에만 렌더하면 목록 영역이 224px 좁아지며 카드/행이 밀리고,
              그러면 더블클릭의 두 번째 클릭이 카드를 벗어나 배경에 떨어져 폴더 진입이 실패한다. */}
          <div className="w-56 flex-shrink-0 border-l border-gray-800 overflow-y-auto">
          {!(singleSelected && !multiChecked) && (
            <p className="p-4 text-xs text-gray-600 text-center">항목을 선택하면<br />상세 정보가 표시됩니다.</p>
          )}
          {singleSelected && !multiChecked && (
            <div className="p-4 space-y-4">
              <div className="flex flex-col items-center gap-2 w-full">
                {isImage(singleSelected) && singleSelected.thumbnailLink ? (
                  <img src={singleSelected.thumbnailLink} alt={singleSelected.name} className="w-full aspect-[4/3] object-cover rounded-lg" />
                ) : (
                  <div className="w-full aspect-[4/3] flex items-center justify-center bg-gray-800 rounded-lg">
                    <FileIcon file={singleSelected} size={40} />
                  </div>
                )}
                <p className="text-sm font-medium text-gray-200 text-center break-all">{singleSelected.name}</p>
              </div>
              <div className="text-xs text-gray-500 space-y-1.5">
                <div><span className="text-gray-400">유형</span><p>{getExt(singleSelected) || singleSelected.mimeType?.split('/')[1] || '—'}</p></div>
                {singleSelected.size && <div><span className="text-gray-400">크기</span><p>{fmtSize(parseInt(singleSelected.size))}</p></div>}
                <div><span className="text-gray-400">수정일</span><p>{fmtDate(singleSelected.modifiedTime)}</p></div>
              </div>
              <div className="space-y-1.5">
                {isFolder(singleSelected) ? (
                  <button onClick={() => openFolder(singleSelected)} className="w-full py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-sm text-gray-200">열기</button>
                ) : (
                  <>
                    <button onClick={() => openPreview(singleSelected)} className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-sm text-gray-200">
                      <Eye size={13} /> 미리보기
                    </button>
                    <button onClick={() => handleDownload(singleSelected)} className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-sm text-gray-200">
                      <Download size={13} /> {isGoogleDoc(singleSelected) ? '열기' : '다운로드'}
                    </button>
                  </>
                )}
                <button onClick={() => handleShare(singleSelected)} className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-sm text-gray-200">
                  <Link2 size={13} /> 공유 링크 복사
                </button>
                <button onClick={() => { setRenaming(singleSelected.id); setRenameVal(singleSelected.name); }} className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-sm text-gray-200">
                  <Edit2 size={13} /> 이름 바꾸기
                </button>
                <button onClick={() => handleStar(singleSelected)} className={`w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-sm transition-colors ${singleSelected.starred ? 'text-yellow-400' : 'text-gray-200'}`}>
                  <Star size={13} fill={singleSelected.starred ? 'currentColor' : 'none'} /> {singleSelected.starred ? '별표 제거' : '별표'}
                </button>
                <button onClick={() => handleDelete(singleSelected)} className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg hover:bg-red-900/30 text-sm text-gray-500 hover:text-red-400">
                  <Trash2 size={13} /> 삭제
                </button>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* 우클릭 컨텍스트 메뉴 */}
      {contextMenu && (
        <div className="fixed z-50 bg-gray-900 border border-gray-700 rounded-xl shadow-xl py-1 min-w-[160px] text-sm"
          style={{ top: contextMenu.y, left: contextMenu.x }} onClick={e => e.stopPropagation()}>
          {isFolder(contextMenu.file) ? (
            <button onClick={() => { openFolder(contextMenu.file); setContextMenu(null); }} className="w-full flex items-center gap-2 px-4 py-2 hover:bg-gray-800 text-gray-200"><Folder size={13} /> 열기</button>
          ) : (
            <>
              <button onClick={() => { openPreview(contextMenu.file); setContextMenu(null); }} className="w-full flex items-center gap-2 px-4 py-2 hover:bg-gray-800 text-gray-200"><Eye size={13} /> 미리보기</button>
              <button onClick={() => { handleDownload(contextMenu.file); setContextMenu(null); }} className="w-full flex items-center gap-2 px-4 py-2 hover:bg-gray-800 text-gray-200"><Download size={13} /> {isGoogleDoc(contextMenu.file) ? '열기' : '다운로드'}</button>
            </>
          )}
          <button onClick={() => { handleShare(contextMenu.file); setContextMenu(null); }} className="w-full flex items-center gap-2 px-4 py-2 hover:bg-gray-800 text-gray-200"><Link2 size={13} /> 공유 링크 복사</button>
          <button onClick={() => { setRenaming(contextMenu.file.id); setRenameVal(contextMenu.file.name); setCheckedIds(new Set([contextMenu.file.id])); setContextMenu(null); }} className="w-full flex items-center gap-2 px-4 py-2 hover:bg-gray-800 text-gray-200"><Edit2 size={13} /> 이름 바꾸기</button>
          <button onClick={() => { handleStar(contextMenu.file); setContextMenu(null); }} className={`w-full flex items-center gap-2 px-4 py-2 hover:bg-gray-800 ${contextMenu.file.starred ? 'text-yellow-400' : 'text-gray-200'}`}>
            <Star size={13} fill={contextMenu.file.starred ? 'currentColor' : 'none'} /> {contextMenu.file.starred ? '별표 제거' : '별표'}
          </button>
          <div className="border-t border-gray-800 my-1" />
          <button onClick={() => { handleDelete(contextMenu.file); setContextMenu(null); }} className="w-full flex items-center gap-2 px-4 py-2 hover:bg-gray-800 text-red-400"><Trash2 size={13} /> 휴지통으로 이동</button>
        </div>
      )}

      {/* 미리보기 모달 */}
      {preview && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="relative max-w-4xl w-full flex flex-col rounded-xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between bg-gray-900 px-4 py-2.5 border-b border-gray-700">
              <div className="flex items-center gap-2 min-w-0">
                {getExt(preview) && (
                  <span className={`text-[10px] font-bold text-white px-2 py-0.5 rounded flex-shrink-0 ${EXT_COLORS[getExt(preview)] || 'bg-gray-600'}`}>
                    {getExt(preview)}
                  </span>
                )}
                <span className="text-gray-100 text-sm font-medium truncate">{preview.name}</span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                <button
                  onClick={() => handleDownload(preview)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-gray-800 text-xs font-medium hover:bg-gray-100 transition-colors"
                >
                  <Download size={12} /> {isGoogleDoc(preview) ? '열기' : '다운로드'}
                </button>
                <button
                  onClick={() => setPreview(null)}
                  className="p-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-white transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
            {isImage(preview) && preview.thumbnailLink ? (
              <img
                src={bigThumb(preview.thumbnailLink)}
                alt={preview.name}
                className="max-h-[80vh] max-w-full object-contain bg-gray-950"
              />
            ) : (
              <div className="w-full h-[75vh] bg-gray-900">
                <iframe
                  src={`https://drive.google.com/file/d/${preview.id}/preview`}
                  className="w-full h-full border-0"
                  allow="autoplay"
                  title={preview.name}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* 멀티셀렉트 플로팅 액션 바 */}
      {multiChecked && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl px-5 py-3">
          <span className="text-sm text-gray-300 font-medium">{checkedIds.size}개 선택됨</span>
          <div className="w-px h-4 bg-gray-700" />
          <button
            onClick={openMoveModal}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-sm text-gray-200 transition-colors"
          >
            <Move size={13} /> 이동
          </button>
          <button
            onClick={handleBatchDelete}
            disabled={deleting}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-900/40 hover:bg-red-900/60 text-sm text-red-400 hover:text-red-300 transition-colors disabled:opacity-60"
          >
            {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
            삭제
          </button>
          <button onClick={() => setCheckedIds(new Set())} className="p-1.5 rounded-lg hover:bg-gray-800 text-gray-500 hover:text-gray-300">
            <X size={13} />
          </button>
        </div>
      )}

      {/* 이동 대상 폴더 선택 모달 */}
      {moveModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setMoveModal(false)}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-80 max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()}>
            {/* 헤더 */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
              <h3 className="text-sm font-semibold text-gray-200">{checkedIds.size}개 항목 이동</h3>
              <button onClick={() => setMoveModal(false)} className="p-1 rounded hover:bg-gray-800 text-gray-500"><X size={14} /></button>
            </div>
            {/* 브레드크럼 */}
            <div className="flex items-center gap-1 px-4 py-2 text-xs border-b border-gray-800 overflow-x-auto whitespace-nowrap">
              <button
                onClick={() => pickerGoTo(-1)}
                className={`hover:text-gray-200 flex-shrink-0 ${pickerStack.length === 0 ? 'text-gray-200 font-medium' : 'text-gray-500'}`}
              >
                내 드라이브
              </button>
              {pickerStack.map((f, i) => (
                <span key={f.id} className="flex items-center gap-1 flex-shrink-0">
                  <ChevronRight size={10} className="text-gray-600" />
                  <button
                    onClick={() => pickerGoTo(i)}
                    className={`hover:text-gray-200 ${i === pickerStack.length - 1 ? 'text-gray-200 font-medium' : 'text-gray-500'}`}
                  >
                    {f.name}
                  </button>
                </span>
              ))}
            </div>
            {/* 폴더 목록 */}
            <div className="flex-1 overflow-y-auto p-2 min-h-[120px]">
              {pickerLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="animate-spin text-gray-500" size={20} /></div>
              ) : pickerFiles.length === 0 ? (
                <p className="text-center text-gray-600 text-xs py-8">하위 폴더 없음</p>
              ) : pickerFiles.map(f => (
                <button key={f.id} onClick={() => pickerOpenFolder(f)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-800 text-sm text-gray-200 transition-colors text-left">
                  <Folder size={15} className="text-yellow-400 flex-shrink-0" />
                  <span className="truncate flex-1">{f.name}</span>
                  <ChevronRight size={12} className="text-gray-600 flex-shrink-0" />
                </button>
              ))}
            </div>
            {/* 현재 위치 표시 + 확인 버튼 */}
            <div className="px-4 py-3 border-t border-gray-800">
              <p className="text-xs text-gray-500 mb-2">
                이동 위치: <span className="text-gray-300">
                  {pickerStack.length === 0 ? '내 드라이브' : pickerStack[pickerStack.length - 1].name}
                </span>
              </p>
              <div className="flex gap-2">
                <button onClick={() => setMoveModal(false)} className="flex-1 py-2 rounded-lg border border-gray-700 text-sm text-gray-300 hover:bg-gray-800 transition-colors">취소</button>
                <button
                  onClick={handleMoveConfirm}
                  disabled={moving}
                  className="flex-1 py-2 rounded-lg text-sm text-white font-medium transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5"
                  style={{ backgroundColor: '#00B2FF' }}
                >
                  {moving ? <Loader2 size={13} className="animate-spin" /> : <Move size={13} />}
                  여기로 이동
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
